import { EMPTY, tap } from "rxjs";
import { inject, Injectable } from "@angular/core";

import { PaymentPayload } from "../../interfaces";

import { HttpService, NetworkStatusService, PaymentMethod } from "src/app/core";

@Injectable({
  providedIn: "root",
})
export class PaymentService extends HttpService {
  protected override get baseUrl(): string {
    return "payment/";
  }

  readonly #network = inject(NetworkStatusService);

  /**
   * Taking a payment. **Refused outright while the server is unreachable** (T-17).
   *
   * There is no offline equivalent of a payment authorization, and every branch below either writes
   * the gateway's HTML into the document or navigates away — so offline this does not fail politely,
   * it fails after a timeout having already committed the clerk to a screen that cannot come back.
   * Refusing up front is the difference between "try again when the connection returns" and a
   * half-navigated terminal with a visitor waiting.
   *
   * This is the choke point every payment goes through, which is why the check is here rather than in
   * the several screens that open the payment dialog.
   */
  addPayment(body: Partial<PaymentPayload>) {
    if (this.#network.isOffline()) {
      this.toastrService.error(this.translation.instant("SYNC.PAYMENT_OFFLINE"));

      // `EMPTY`, not `throwError`: every call site subscribes without an error callback
      // (`ticket.component`, `invoice.component`, the four membership screens), and RxJS rethrows an
      // unhandled error asynchronously — so refusing with an error would put an uncaught exception
      // through Angular's `ErrorHandler` on every offline payment attempt. Completing without
      // emitting gives those callers exactly what an error would have: their `next` never runs, so
      // nothing navigates or refreshes, and the toast above is what the clerk acts on.
      return EMPTY;
    }

    return this.post<string>({ APIName: "pay", body }).pipe(
      tap((data) => {
        if (!data) return;

        if (body.paymentMethod === PaymentMethod.Online) {
          if (body.paymentMethod && data.trimStart().startsWith("<")) {
            // The API returned the payment provider page as HTML; render it in the same tab and auto-submit its form.
            const html = data.replace("</body>", `<script>document.getElementById("paymentForm")?.submit();</script></body>`);

            document.open();
            document.write(html);
            document.close();
          } else {
            window.location.reload();
          }
        } else {
          // navigating to the payment provider page.
          const id = data.split("?id=").pop();
          const URL = data.split("?id=").shift();

          if (id) localStorage.setItem("requestId", btoa(id));
          if (body.paymentMethod) window.open(window.location.origin.includes("localhost") ? data : URL, "_self");
          else window.location.reload();
        }
      }),
    );
  }
}
